import * as THREE from 'three';
import { useEffect, useRef, useState } from 'react';

export function AmbientScene({ dense = false, light = false, className = '' }: { dense?: boolean; light?: boolean; className?: string }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [rendererLabel, setRendererLabel] = useState('WEBGL / SHADER FIELD');
  const videoUrl = import.meta.env.VITE_AMBIENT_VIDEO_URL as string | undefined;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    } catch {
      setRendererLabel('CSS / REDUCED MOTION');
      return undefined;
    }
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(38, 1, .1, 1000);
    camera.position.set(0, 0, 11);
    const root = new THREE.Group();
    scene.add(root);
    scene.fog = new THREE.FogExp2(light ? 0xf1f6f9 : 0x090909, dense ? .055 : .035);

    const colors = [0xf6a4bf, 0x9b87ff, 0x8fe8d7, 0xf3ed88, 0xffffff];
    const points = new THREE.BufferGeometry();
    const positions = new Float32Array((dense ? 1100 : 720) * 3);
    const colorArray = new Float32Array(positions.length);
    for (let i = 0; i < positions.length; i += 3) {
      const radius = 3.5 + Math.random() * 5.2;
      const theta = Math.random() * Math.PI * 2;
      const y = (Math.random() - .5) * 8;
      positions[i] = Math.cos(theta) * radius + (Math.random() - .5) * 1.8;
      positions[i + 1] = y;
      positions[i + 2] = -Math.random() * 7 - 1;
      const color = new THREE.Color(colors[Math.floor(Math.random() * colors.length)]);
      colorArray[i] = color.r;
      colorArray[i + 1] = color.g;
      colorArray[i + 2] = color.b;
    }
    points.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    points.setAttribute('color', new THREE.BufferAttribute(colorArray, 3));
    const particleMaterial = new THREE.PointsMaterial({ size: dense ? .025 : .035, vertexColors: true, transparent: true, opacity: .62, blending: THREE.AdditiveBlending, depthWrite: false });
    const particleField = new THREE.Points(points, particleMaterial);
    root.add(particleField);

    const blobGeometry = new THREE.IcosahedronGeometry(1.45, 4);
    const blobs: Array<{ mesh: THREE.Mesh; phase: number; base: THREE.Vector3 }> = [];
    const blobColors = light ? [0xffa9c1, 0xc6bfff, 0xa9e9dc] : dense ? [0xf3a6c0, 0xb2a7ff, 0x91ead4] : [0xf4b0c7, 0xaba0ff];
    blobColors.forEach((color, index) => {
      const material = new THREE.MeshPhysicalMaterial({ color, roughness: .25, metalness: .08, transmission: .18, thickness: .7, transparent: true, opacity: .78, emissive: color, emissiveIntensity: .08 });
      const mesh = new THREE.Mesh(blobGeometry, material);
      const base = new THREE.Vector3((index - 1) * 2.8, (index % 2 ? -1.1 : .85), -1.3 - index * .7);
      mesh.position.copy(base);
      mesh.scale.setScalar(index === 0 ? 1.08 : .66);
      root.add(mesh);
      blobs.push({ mesh, phase: index * 1.9, base });
    });

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(2.8, .008, 8, 160),
      new THREE.MeshBasicMaterial({ color: 0xf6a4bf, transparent: true, opacity: .32, blending: THREE.AdditiveBlending }),
    );
    ring.rotation.x = Math.PI * .46;
    ring.position.z = -2.5;
    root.add(ring);
    root.add(new THREE.AmbientLight(0xffffff, 1.2));
    const keyLight = new THREE.PointLight(0xffb3ce, 22, 12);
    keyLight.position.set(-3, 3, 4);
    root.add(keyLight);
    const fillLight = new THREE.PointLight(0x9e91ff, 15, 10);
    fillLight.position.set(4, -2, 2);
    root.add(fillLight);

    const pointer = { x: 0, y: 0 };
    const onPointer = (event: PointerEvent) => {
      pointer.x = (event.clientX / window.innerWidth - .5) * 2;
      pointer.y = (event.clientY / window.innerHeight - .5) * 2;
    };
    window.addEventListener('pointermove', onPointer, { passive: true });
    const resize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
      renderer.setSize(width, height, false);
      camera.aspect = width / Math.max(1, height);
      camera.updateProjectionMatrix();
    };
    resize();
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let frame = 0;
    let raf = 0;
    const tick = (time: number) => {
      const t = reduced ? 0 : time * .001;
      particleField.rotation.y = t * .018 + pointer.x * .025;
      particleField.rotation.x = pointer.y * .018;
      root.position.x += (pointer.x * .16 - root.position.x) * .025;
      root.position.y += (-pointer.y * .12 - root.position.y) * .025;
      blobs.forEach(({ mesh, phase, base }) => {
        mesh.position.x = base.x + Math.sin(t * .48 + phase) * .12;
        mesh.position.y = base.y + Math.cos(t * .62 + phase) * .16;
        mesh.rotation.x = t * (.06 + phase * .01);
        mesh.rotation.y = t * (.1 + phase * .02);
        const pulse = 1 + Math.sin(t * 1.35 + phase) * .045;
        mesh.scale.setScalar((base.x === 0 ? 1.08 : .66) * pulse);
      });
      ring.rotation.z = t * .04;
      ring.scale.setScalar(1 + Math.sin(t * .5) * .03);
      renderer.render(scene, camera);
      frame += 1;
      if (frame === 24) setRendererLabel((navigator as Navigator & { gpu?: unknown }).gpu ? 'WEBGL2 / GPU FIELD · WEBGPU READY' : 'WEBGL2 / GPU FIELD');
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    window.addEventListener('resize', resize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointermove', onPointer);
      renderer.dispose();
      blobGeometry.dispose();
      points.dispose();
      particleMaterial.dispose();
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
      blobs.forEach(({ mesh }) => (mesh.material as THREE.Material).dispose());
    };
  }, [dense, light]);

  return <div className={`ambient-scene ${light ? 'light' : ''} ${className}`} aria-hidden="true">
    {videoUrl ? <video className="ambient-video" src={videoUrl} autoPlay muted loop playsInline /> : null}
    <canvas ref={canvasRef} className="ambient-canvas" />
    <div className="ambient-vignette" />
    <span className="render-badge">{rendererLabel}</span>
  </div>;
}
